import * as backendServices from '../../../../services/backendServices';
import { BusinessDocType } from '../../../../types';
import { escape } from '../../../../utils/escape';

export type ExtractedPONumbers = {
    headerPONumber: string | null;
    lineItems: {
        lineItemIdx: number;
        poNumber: string | null;
    }[];
};

const emptyPONumbers: ExtractedPONumbers = {
    headerPONumber: null,
    lineItems: [],
};

export function extractPONumbers(document: backendServices.ViewModels.DocumentViewModel): ExtractedPONumbers {
    // PO numbers are only meaningful on Invoices. For any other BusinessDocType
    // the `Invoice` branch of BusinessDocFields is not populated, so bail out.
    if (document.BusinessDocType !== BusinessDocType.Invoice) {
        return emptyPONumbers;
    }

    const headerPONumber = document.BusinessDocFields.Invoice.PurchaseOrder || null;

    const lineItemPONumbers = document.BusinessDocFields.Invoice.InvoiceLineItems.map((lineItem, lineItemIdx) => ({
        lineItemIdx,
        poNumber: lineItem.PurchaseOrderNum || null,
    }));

    return {
        headerPONumber,
        lineItems: lineItemPONumbers,
    };
}

async function fetchPODocument({
    poNumber,
    buyerCompanyId,
    childCompanyId,
}: {
    poNumber: string;
    buyerCompanyId: number;
    childCompanyId?: string;
}): Promise<backendServices.ViewModels.DocumentViewModel | null> {
    const api = new backendServices.Apis.DocumentApi();

    const { data: poDocuments } = await api.getDocumentsForCompany({
        companyID: buyerCompanyId,
        childCompanyId: childCompanyId,
        isBuyer: true,
        $filter: `BusinessDocType eq ${BusinessDocType.PurchaseOrder} and PurchaseOrderNumber eq '${escape(poNumber)}'`,
    });

    if (poDocuments.Items.length === 0) {
        return null;
    }

    const documentId = poDocuments.Items[0].DocumentID;

    const { data: poDocument } = await api.getDocument(documentId);

    return poDocument;
}

export type PODocumentsForInvoice = {
    headerPODocument: backendServices.ViewModels.DocumentViewModel | null;
    lineItemPODocuments: {
        lineItemIdx: number;
        poDocument: backendServices.ViewModels.DocumentViewModel | null;
    }[];
};

export async function fetchPODocumentsForInvoice(
    poNumbers: ExtractedPONumbers,
    buyerCompanyId: number,
    childCompanyId?: string
): Promise<PODocumentsForInvoice> {
    // Used to avoid duplicate calls to the API
    const poRecord = {} as Record<string, backendServices.ViewModels.DocumentViewModel | null>;

    const headerPODocument = poNumbers.headerPONumber
        ? await fetchPODocument({
              poNumber: poNumbers.headerPONumber!,
              buyerCompanyId,
              childCompanyId,
          })
        : null;

    if (poNumbers.headerPONumber) {
        poRecord[poNumbers.headerPONumber] = headerPODocument;
    }

    const lineItemPODocuments = await Promise.all(
        poNumbers.lineItems.map(async ({ lineItemIdx, poNumber }) => {
            if (!poNumber) {
                return {
                    lineItemIdx,
                    poDocument: null,
                };
            }

            if (poNumber in poRecord) {
                return {
                    lineItemIdx,
                    poDocument: poRecord[poNumber],
                };
            }

            const poDocument = await fetchPODocument({
                poNumber,
                buyerCompanyId,
                childCompanyId,
            });

            poRecord[poNumber] = poDocument;

            return {
                lineItemIdx,
                poDocument,
            };
        })
    );

    return {
        headerPODocument,
        lineItemPODocuments,
    };
}
